/* eslint-disable max-lines-per-function */
import { get, omit } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createSearchParams, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useWizard } from '../../../features/Wizard';
import { FORM, STRINGS } from '../../../constants';
import { locationsApis, vouchersApis } from '../../../apis';
import { REFERENCE_NAME } from '../constants';
import * as AppActions from '../../../redux/appActions';
import { ExportReasonsActions } from '../../../redux/exportReasonSlice';
import { networksActions } from '../../../redux/networksSlice';
import { useCustomSnackbar } from '../../../features/CustomSnackbar';
import { StringUtil } from '../../../utils';
import addressBooksSlice from '../../../redux/addressBooksSlice';
import { BasketSelectors } from '../../../redux/basket';

export const useReferencesLoader = () => {
  const { values, references, setReferences } = useWizard();
  const dispatch = useDispatch();
  const snackbar = useCustomSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const basketItem = useSelector(state =>
    BasketSelectors.getBasketItemById(state, searchParams.get('basketItemId'))
  );

  const loadDropOffCollection = useCallback(async () => {
    const collectionPickup = references[REFERENCE_NAME.COLLECTION_PICKUP];
    const dropOffPickupLocationCode = get(
      values,
      FORM.SHIPMENT_FIELDS.DROP_OFF_PICKUP_LOCATION_CODE.KEY
    );

    if (dropOffPickupLocationCode && !collectionPickup) {
      const collectionPickupResult = await locationsApis.getPickupLocationsById(
        dropOffPickupLocationCode
      );

      setReferences(REFERENCE_NAME.COLLECTION_PICKUP, {
        pickupLocation: collectionPickupResult.data,
      });
    }
  }, [references.collectionPickup, setReferences, values]);

  const loadDeliveryPickup = useCallback(async () => {
    const deliveryPickup = references[REFERENCE_NAME.DELIVERY_PICKUP];
    const pickupLocationCode = get(
      values,
      FORM.SHIPMENT_FIELDS.PICKUP_LOCATION_CODE.KEY
    );

    if (pickupLocationCode && !deliveryPickup) {
      const deliveryPickupResult = await locationsApis.getPickupLocationsById(
        pickupLocationCode
      );
      setReferences(REFERENCE_NAME.DELIVERY_PICKUP, {
        pickupLocation: deliveryPickupResult.data,
      });
    }
  }, [references.deliveryPickup, setReferences, values]);

  const loadDeliveryAddress = useCallback(async () => {
    const deliveryAddressReferences =
      references[REFERENCE_NAME.DELIVERY_ADDRESS];
    const deliveryAddressValues = get(
      values,
      FORM.SHIPMENT_FIELDS.DESTINATION_ADDRESS.KEY
    );

    if (!deliveryAddressReferences) {
      const referenceAddress = await dispatch(
        AppActions.fetchAddressDetails(deliveryAddressValues)
      ).unwrap();
      setReferences(REFERENCE_NAME.DELIVERY_ADDRESS, referenceAddress);
    }
  }, [references.deliveryAddress, setReferences, values, dispatch]);

  const loadCollectionAddress = useCallback(async () => {
    const collectionAddressReferences =
      references[REFERENCE_NAME.COLLECTION_ADDRESS];
    const collectionAddressValues = get(
      values,
      FORM.SHIPMENT_FIELDS.COLLECTION_ADDRESS.KEY
    );

    if (!collectionAddressReferences) {
      const referenceAddress = await dispatch(
        AppActions.fetchAddressDetails(collectionAddressValues)
      ).unwrap();
      setReferences(REFERENCE_NAME.COLLECTION_ADDRESS, referenceAddress);
    }
  }, [references.collectionAddress, setReferences, values]);

  const loadShipmentAddressBookByType = useCallback(
    async (referenceName, addressBookId) => {
      const existedAddressBook = get(references, referenceName, {});

      if (addressBookId) {
        if (existedAddressBook?.addressBookId === addressBookId) {
          return existedAddressBook;
        }

        const addressBook = await dispatch(
          addressBooksSlice.actions.getAddressBookById(addressBookId)
        ).unwrap();

        setReferences(referenceName, addressBook);
        return addressBook;
      }

      return existedAddressBook;
    },
    [references, dispatch, setReferences, basketItem]
  );

  const loadDeliveryAddressBook = useCallback(
    () =>
      loadShipmentAddressBookByType(
        REFERENCE_NAME.DELIVERY_ADDRESS_BOOK,
        get(
          basketItem?.shipment,
          FORM.SHIPMENT_FIELDS.DELIVERY_ADDRESS_BOOK_ID.KEY,
          ''
        )
      ),
    [basketItem]
  );

  const loadCollectionAddressBook = useCallback(
    () =>
      loadShipmentAddressBookByType(
        REFERENCE_NAME.COLLECTION_ADDRESS_BOOK,
        get(
          basketItem?.shipment,
          FORM.SHIPMENT_FIELDS.COLLECTION_ADDRESS_BOOK_ID.KEY,
          ''
        )
      ),
    [basketItem]
  );

  const loadExportReasons = useCallback(async () => {
    try {
      const exportReasons = await dispatch(
        ExportReasonsActions.fetchExportReasons()
      ).unwrap();
      setReferences(REFERENCE_NAME.EXPORT_REASON, exportReasons);
    } catch (error) {
      snackbar.showError({
        message: StringUtil.formatMessage(
          STRINGS.FAILED_TO_GET_$,
          'reasons for export'
        ),
        persist: true,
      });
      setReferences(REFERENCE_NAME.EXPORT_REASON, []);
    }
  }, [dispatch, setReferences]);

  const loadNetworks = useCallback(
    (query, errorMessage = null) =>
      dispatch(networksActions.fetchNetworks(query))
        .unwrap()
        .then(networks => {
          setReferences(REFERENCE_NAME.NETWORKS, networks);
          return networks;
        })
        .catch(() => {
          snackbar.showError({
            message: errorMessage || STRINGS.NO_NETWORKS_ERROR,
            persist: true,
          });
          setReferences(REFERENCE_NAME.NETWORKS, []);
        }),
    [dispatch, setReferences, snackbar.showError]
  );

  const removeVoucherIdFromQueryParams = useCallback(() => {
    const queryParams = Object.fromEntries(searchParams);

    navigate(
      {
        pathname: location.pathname,
        search: createSearchParams(omit(queryParams, 'voucherId')).toString(),
      },
      {
        replace: true,
      }
    );
  }, [searchParams, navigate]);

  const deleteVoucher = useCallback(() => {
    removeVoucherIdFromQueryParams();
    setReferences(REFERENCE_NAME.VOUCHER, null);
  }, [removeVoucherIdFromQueryParams, setReferences]);

  const loadVoucher = useCallback(async () => {
    const { voucherId } = Object.fromEntries(searchParams);
    try {
      const voucher = await vouchersApis.getVoucherById(voucherId);
      setReferences(REFERENCE_NAME.VOUCHER, voucher);
    } catch (err) {
      deleteVoucher();
      snackbar.showWarning({
        message: STRINGS.INVALID_VOUCHER,
      });
    }
  }, [searchParams, setReferences, deleteVoucher, snackbar]);

  return {
    loadDropOffCollection,
    loadDeliveryPickup,
    loadCollectionAddress,
    loadDeliveryAddressBook,
    loadCollectionAddressBook,
    loadShipmentAddressBookByType,
    loadDeliveryAddress,
    loadExportReasons,
    loadNetworks,
    loadVoucher,
    deleteVoucher,
  };
};
